import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Command as CommandPrimitive } from "cmdk";
import { Search, X, CornerDownLeft } from "lucide-react";
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
} from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { useUserRole } from "@/hooks/useUserRole";
import { buildUserSidebar, type SidebarSectionConfig } from "./userSidebarConfig";
import { adminSidebarConfig } from "./adminSidebarConfig";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Premium glassmorphic ⌘K command palette.
 * Custom Dialog markup (not shadcn CommandDialog) so the panel can be fully
 * styled and made responsive without touching shared shadcn primitives.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAdmin } = useUserRole();

  const sections = useMemo<SidebarSectionConfig[]>(() => {
    const userSections = buildUserSidebar({ isAdmin });
    return isAdmin ? [...userSections, ...adminSidebarConfig] : userSections;
  }, [isAdmin]);

  const handleSelect = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-background/70 backdrop-blur-md" />
        <DialogPrimitive.Content
          className={cn(
            "fixed z-50 outline-none",
            // Mobile: top sheet
            "inset-x-2 top-2 max-h-[88vh]",
            // Tablet+: centered floating panel
            "sm:inset-x-auto sm:left-1/2 sm:top-[12vh] sm:-translate-x-1/2 sm:w-[min(640px,92vw)] sm:max-h-[70vh]",
            // Surface
            "rounded-2xl border border-white/10 bg-card/85 backdrop-blur-2xl",
            "shadow-[0_24px_80px_-12px_hsl(240_50%_2%/0.7),0_0_0_1px_hsl(0_0%_100%/0.04)_inset]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "motion-reduce:!animate-none",
            "overflow-hidden"
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            {t("commandPalette.placeholder")}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Search and navigate to any page in the platform.
          </DialogPrimitive.Description>

          {/* Subtle gradient accent ring */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/[0.06] via-transparent to-secondary/[0.06]"
          />

          <CommandPrimitive
            className="relative flex h-full max-h-[inherit] flex-col"
            loop
          >
            {/* Header / input */}
            <div className="flex items-center gap-3 border-b border-white/5 px-4 sm:px-5 py-3.5">
              <Search className="h-5 w-5 shrink-0 text-foreground/50" />
              <CommandPrimitive.Input
                placeholder={t("commandPalette.placeholder")}
                className={cn(
                  "flex-1 bg-transparent text-[15px] text-foreground outline-none",
                  "placeholder:text-foreground/40"
                )}
              />
              <kbd
                className={cn(
                  "hidden sm:inline-flex h-6 select-none items-center gap-1 rounded-md",
                  "border border-white/10 bg-white/[0.04] px-1.5 font-mono text-[10px] font-medium text-foreground/60"
                )}
              >
                ESC
              </kbd>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                aria-label="Close"
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-lg",
                  "text-foreground/60 hover:text-foreground hover:bg-white/5 transition-colors"
                )}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Results */}
            <CommandPrimitive.List
              className={cn(
                "min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2",
                "scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent"
              )}
            >
              <CommandPrimitive.Empty className="py-10 text-center text-sm text-foreground/50">
                {t("commandPalette.empty")}
              </CommandPrimitive.Empty>

              {sections.map((section, idx) => (
                <CommandPrimitive.Group
                  key={section.labelKey}
                  heading={t(section.labelKey)}
                  className={cn(
                    "px-1",
                    idx > 0 && "mt-2 pt-2 border-t border-white/[0.04]",
                    "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1.5",
                    "[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold",
                    "[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.18em]",
                    "[&_[cmdk-group-heading]]:text-foreground/40"
                  )}
                >
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const label = t(item.labelKey);
                    return (
                      <CommandPrimitive.Item
                        key={item.to}
                        value={`${label} ${item.to} ${t(section.labelKey)}`}
                        onSelect={() => handleSelect(item.to)}
                        className={cn(
                          "group relative flex cursor-pointer select-none items-center gap-3",
                          "rounded-xl px-2.5 py-2.5 text-sm text-foreground/85",
                          "transition-colors duration-150",
                          "data-[selected=true]:bg-gradient-to-r data-[selected=true]:from-primary/15 data-[selected=true]:via-primary/10 data-[selected=true]:to-secondary/10",
                          "data-[selected=true]:text-foreground",
                          "data-[selected=true]:shadow-[inset_2px_0_0_0_hsl(var(--primary))]",
                          "aria-disabled:pointer-events-none aria-disabled:opacity-50"
                        )}
                      >
                        <span
                          className={cn(
                            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                            "border border-white/5 bg-white/[0.03]",
                            "group-data-[selected=true]:border-primary/30 group-data-[selected=true]:bg-primary/15"
                          )}
                        >
                          <Icon className="h-4 w-4 text-foreground/70 group-data-[selected=true]:text-primary" />
                        </span>
                        <span className="min-w-0 flex-1 truncate font-medium">
                          {label}
                        </span>
                        <kbd
                          className={cn(
                            "hidden sm:inline-flex shrink-0 items-center rounded-md",
                            "border border-white/5 bg-white/[0.03] px-1.5 py-0.5",
                            "font-mono text-[10px] text-foreground/45",
                            "group-data-[selected=true]:border-primary/20 group-data-[selected=true]:text-foreground/70"
                          )}
                        >
                          {item.to}
                        </kbd>
                        <CornerDownLeft
                          className={cn(
                            "h-3.5 w-3.5 shrink-0 text-foreground/40 opacity-0",
                            "group-data-[selected=true]:opacity-100"
                          )}
                        />
                      </CommandPrimitive.Item>
                    );
                  })}
                </CommandPrimitive.Group>
              ))}
            </CommandPrimitive.List>

            {/* Footer hint */}
            <div className="hidden sm:flex items-center justify-between gap-3 border-t border-white/5 px-4 py-2.5 text-[11px] text-foreground/45">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5">
                  <kbd className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px]">↑↓</kbd>
                  navigate
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <kbd className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px]">↵</kbd>
                  open
                </span>
              </div>
              <span className="inline-flex items-center gap-1.5">
                <kbd className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono text-[10px]">⌘K</kbd>
                toggle
              </span>
            </div>
          </CommandPrimitive>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

/**
 * Hook to wire ⌘K / Ctrl+K to open the palette globally.
 */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return { open, setOpen };
}
